import { type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { StatusBadge } from "../../components/index.ts";
import { useEvent } from "./use-events.ts";
import {
  decisionLevel,
  severityLabel,
  severityLevel,
  traceHref,
  type EventRow,
} from "./vocab.ts";
import { isoAttr, formatDateTime } from "../../lib/datetime.ts";
import "./events.css";

/**
 * The Event detail screen (task 11.12; design 12.2 Events row, 12.3 drill-down). It is the drill
 * target of a row in the explorer (`/events/{id}`) and shows one event's full REDACTED detail from
 * `GET /api/v1/events/{id}` (role `viewer`). It links to the event's trace (`/traces/{id}`) when it
 * carries one, so an operator can pivot from a single event to its cross-service waterfall.
 *
 * The detail read is keyed on the event id (an event is immutable once recorded), so it does not
 * depend on the live/pause state — opening an event shows that exact event regardless of the
 * time-range. It handles every state a screen must (task 11 preamble; design 12.3): restricted
 * (viewer-level; an unknown/below-viewer role sees the restricted state), a missing id as an honest
 * empty state, loading / error / ready, a 404 as a distinct empty cause, and a cold-core waking
 * error deferring to the shell's banner.
 */
export function EventDetailScreen(): ReactNode {
  const { id } = useParams();
  const { role } = useIdentity();
  const hasId = id !== undefined && id.length > 0;

  const { query } = useEvent(id, { enabled: hasId });
  const state = resolveState(hasId, role, query);

  return (
    <ScreenScaffold
      title="Event"
      subtitle="One redacted event in full. Fields are closed to the catalog and never contain a secret-like value; link through to the event's trace when it has one."
      state={state}
    >
      <p className="evt-back">
        <Link to="/events">← Back to the events explorer</Link>
      </p>
      {query.data !== undefined && <EventDetailBody event={query.data} />}
    </ScreenScaffold>
  );
}

function resolveState(
  hasId: boolean,
  role: ReturnType<typeof useIdentity>["role"],
  query: ReturnType<typeof useEvent>["query"],
): ScreenState {
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so this event is hidden until it is. The server enforces access regardless."
          : "The events view needs at least the viewer role.",
    };
  }

  if (!hasId) {
    return {
      kind: "empty",
      cause: "No event was named in the address. Choose an event from the explorer to see its detail.",
    };
  }

  if (query.data !== undefined) return { kind: "ready" };

  if (query.isError) {
    if (query.error.isWaking) return { kind: "loading" };
    if (query.error.kind === "not_found") {
      return {
        kind: "empty",
        cause: "There is no such event, or it has aged out of retention. It may have expired since the explorer was loaded.",
      };
    }
    return { kind: "error", message: errorMessage(query.error) };
  }

  return { kind: "loading" };
}

function errorMessage(error: ReturnType<typeof useEvent>["query"]["error"]): string {
  if (error === null) return "This event could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see this event. The server enforces access.";
    case "network":
      return "The event request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}

function EventDetailBody(props: { event: EventRow }): ReactNode {
  const { event } = props;
  const attrs = (event.attrs ?? {}) as Record<string, string | number | boolean>;
  const attrKeys = Object.keys(attrs).sort();

  return (
    <div className="evt-detail">
      <header className="evt-detail__head">
        <h2 className="evt-detail__name">{event.name}</h2>
        <div className="evt-detail__badges">
          <StatusBadge
            level={severityLevel(event.severity)}
            label={severityLabel(event.severity)}
            size="sm"
            title={`Severity: ${severityLabel(event.severity)}`}
          />
          {event.decision !== null && event.decision !== undefined && (
            <StatusBadge
              level={decisionLevel(event.decision)}
              label={event.decision === "deny" ? "Deny" : "Allow"}
              size="sm"
              title={
                event.reason !== null && event.reason !== undefined
                  ? `Decision: ${event.decision}: ${event.reason}`
                  : `Decision: ${event.decision}`
              }
            />
          )}
          {event.sample_rate < 1 && (
            <span
              className="evt-sampled"
              title={`Sampled at ${event.sample_rate}, this event represents about ${Math.round(1 / event.sample_rate)} events`}
            >
              sampled 1:{Math.round(1 / event.sample_rate)}
            </span>
          )}
        </div>
      </header>

      <dl className="evt-detail__fields">
        <Field label="Event id" value={<code>{event.id}</code>} />
        <Field
          label="Occurred"
          value={
            <time dateTime={isoAttr(event.ts)}>
              {formatDateTime(event.ts)}
            </time>
          }
        />
        <Field
          label="Received"
          value={
            <time dateTime={isoAttr(event.recv_ts)}>
              {formatDateTime(event.recv_ts)}
            </time>
          }
        />
        <Field label="Component" value={event.service} />
        <Field label="Source" value={<code>{event.source_id}</code>} />
        <Field label="Instance" value={<code>{event.instance_id}</code>} />
        <OptionalField label="Agent" value={event.agent_sub} mono />
        <OptionalField label="Operation" value={event.operation} mono />
        <OptionalField label="Gateway" value={event.gateway_id} mono />
        <OptionalField label="Reason" value={event.reason} />
        <Field
          label="Trace"
          value={
            event.trace_id !== null && event.trace_id !== undefined ? (
              <Link to={traceHref(event.trace_id)} className="evt-detail__trace">
                <code>{event.trace_id}</code> → waterfall
              </Link>
            ) : (
              <span className="evt-faint">no trace</span>
            )
          }
        />
        <OptionalField label="Span" value={event.span_id} mono />
      </dl>

      <section className="evt-detail__attrs" aria-label="Redacted attributes">
        <h3 className="evt-detail__attrs-title">Attributes</h3>
        {attrKeys.length === 0 ? (
          <p className="evt-faint">This event carries no catalog attributes.</p>
        ) : (
          <dl className="evt-detail__fields">
            {attrKeys.map((key) => (
              <Field key={key} label={key} value={String(attrs[key])} />
            ))}
          </dl>
        )}
      </section>
    </div>
  );
}

function Field(props: { label: string; value: ReactNode }): ReactNode {
  return (
    <div className="evt-detail__field">
      <dt>{props.label}</dt>
      <dd>{props.value}</dd>
    </div>
  );
}

function OptionalField(props: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}): ReactNode {
  const { value } = props;
  if (value === null || value === undefined) {
    return <Field label={props.label} value={<span className="evt-faint">n/a</span>} />;
  }
  return (
    <Field label={props.label} value={props.mono === true ? <code>{value}</code> : value} />
  );
}
